import {
    getTopLevelDomain,
    listTopLevelDomains
} from './queries.js';
import {
    createTopLevelDomain,
    deleteTopLevelDomain,
    updateTopLevelDomain,
} from './mutations';

import {
    generateClient,
    GraphQLQuery,
} from 'aws-amplify/api';
import {TopLevelDomain} from "../types.ts";
import {
    showErrorNotifications,
    showSuccessNotification
} from "../../../components/ServiceNotifications.tsx";


// FNP-1292 — the WHOLE connection, not just its items.
//
// ⚠️ THE OLD SHAPE DECLARED `nextToken` OUT OF EXISTENCE. `fetchRecords`
// returned `.items` and the type had no token member, so the table could never
// page past the first server response no matter how it was wired. The GraphQL
// document was pagination-capable the whole time; the service threw the token
// away. Same defect registrars carried.
export type TopLevelDomainsPage = {
    items: TopLevelDomain[],
    nextToken: string | null,
}

export type ListTopLevelDomains = {
    listTopLevelDomains: TopLevelDomainsPage
}

export type GetTopLevelDomain = {
    getTopLevelDomain: TopLevelDomain
}

export type CreateTopLevelDomain = {
    createTopLevelDomain: TopLevelDomain
}

export type UpdateTopLevelDomain = {
    updateTopLevelDomain: TopLevelDomain
}

export type DeleteTopLevelDomain = {
    deleteTopLevelDomain: string
}

const client = generateClient();

// One server page. A SERVER concern under token pagination, so it is fixed
// here rather than driven by a rows-per-page control.
export const PAGE_SIZE = 50;

export const fetchRecords = async (
    filter: object = {},
    limit: number = PAGE_SIZE,
    nextToken: string | null = null,
): Promise<TopLevelDomainsPage> => {

    try {
        const response = await client.graphql<GraphQLQuery<ListTopLevelDomains>>({
                                                                                     query: (listTopLevelDomains),
                                                                                     variables: {
                                                                                         filter: filter,
                                                                                         limit: limit,
                                                                                         nextToken: nextToken,
                                                                                     },
                                                                                 });
        return {
            items: response.data?.listTopLevelDomains?.items ?? [],
            // ⚠️ NORMALISED TO null. AppSync returns the field absent rather
            // than null at the end of the results, and `undefined` reaching
            // `getNextPageParam` reads the same as a token would not.
            nextToken: response.data?.listTopLevelDomains?.nextToken ?? null,
        };
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
        // ⚠️ RE-THROWN, and this is what makes an error state POSSIBLE.
        // Swallowing it returned `undefined`, so React Query saw a successful
        // query with no rows: "the API is down" and "you have no top level
        // domains" rendered identically and `isError` was permanently false.
        throw e;
    }
};

export const fetch = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<GetTopLevelDomain>>({
                                                                                   query: (getTopLevelDomain),
                                                                                   variables: {id: id},
                                                                               });
        return response.data?.getTopLevelDomain;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const add = async (payload: object) => {
    try {
        const response = await client.graphql<GraphQLQuery<CreateTopLevelDomain>>({
                                                                                      query: (createTopLevelDomain),
                                                                                      variables: {input: payload},
                                                                                  });

        showSuccessNotification();
        return response.data?.createTopLevelDomain;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e);
        throw e;
    }
};

export const update = async (id: string, record: TopLevelDomain) => {

    console.debug('IN UPDATE TOP LEVEL DOMAIN', id, record);

    delete record.created;
    delete record.updated;

    try {
        const response = await client.graphql<GraphQLQuery<UpdateTopLevelDomain>>({
                                                                                      query: (updateTopLevelDomain),
                                                                                      variables: {input: record},
                                                                                  });

        showSuccessNotification();
        return response.data?.updateTopLevelDomain;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const deleteRecord = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<DeleteTopLevelDomain>>({
                                                                                      query: (deleteTopLevelDomain),
                                                                                      variables: {input: {id: id}},
                                                                                  });
        showSuccessNotification();
        return response.data?.deleteTopLevelDomain;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};
